/**
 * API helpers for task dependency endpoints.
 * 
 * Task dependencies create predecessor-successor relationships where
 * a dependent task cannot start until its predecessor completes.
 */

import { fetcher } from '../utils/fetcher';

/**
 * Task dependency model (snake_case to match backend).
 */
export interface TaskDependency {
  id: string;
  dependent_task_id: string;
  predecessor_task_id: string;
  created_at: string;
  updated_at: string;
}

/**
 * Task dependency with task details.
 */
export interface TaskDependencyWithDetails {
  id: string;
  dependent_task_id: string;
  predecessor_task_id: string;
  dependent_task_title: string;
  predecessor_task_title: string;
  predecessor_task_status: string;
  created_at: string;
}

/**
 * Dependency chain response from backend.
 */
export interface DependencyChainResponse {
  task_id: string;
  transitive_predecessors: string[];
  blocking_tasks: string[];
}

/**
 * Create dependency request payload.
 */
export interface TaskDependencyCreate {
  dependent_task_id: string;
  predecessor_task_id: string;
}

const API_URL = '/api/v1/tasks/dependencies';

/**
 * Get all dependencies for a specific task.
 * 
 * @param taskId - ID of the task to get dependencies for
 * @returns Array of task dependencies with details
 */
export const getTaskDependencies = async (taskId: string): Promise<TaskDependencyWithDetails[]> => {
  return await fetcher(`${API_URL}/read/${taskId}`);
};

/**
 * Get the full dependency chain for a task (recursive).
 * Returns all tasks that must be completed before this task.
 * 
 * @param taskId - ID of the task to get the dependency chain for
 * @returns Dependency chain response with transitive predecessors and blocking tasks
 */
export const getDependencyChain = async (taskId: string): Promise<DependencyChainResponse> => {
  return await fetcher(`${API_URL}/read/${taskId}/chain`);
};

/**
 * Create a new task dependency.
 * 
 * @param dependency - Dependency data (dependent_task_id, predecessor_task_id)
 * @returns Created dependency
 * @throws Error if circular dependency detected or tasks are in different projects
 */
export const createDependency = async (dependency: TaskDependencyCreate): Promise<TaskDependency> => {
  return await fetcher(`${API_URL}/create`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(dependency),
  });
};

/**
 * Delete a task dependency.
 * 
 * @param dependencyId - ID of the dependency to delete
 * @returns True if deleted successfully
 */
export const deleteDependency = async (dependencyId: string): Promise<boolean> => {
  return await fetcher(`${API_URL}/delete/${dependencyId}`, {
    method: 'DELETE',
  });
};
